import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LocalizationPipe } from '@shared/modules/localization';
import { DateFormatType } from '@shared/core';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { FormFieldDateModeType, IFormFieldDateConfig } from '../../models';
import { BaseFormFieldComponent, baseProviderComponent } from '../base-form-field.component';

@Component({
  selector: 'ui-form-date',
  templateUrl: './form-date.component.html',
  styleUrl: './form-date.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, FormsModule, NzFormModule, NzDatePickerModule, LocalizationPipe],
  providers: baseProviderComponent(UIFormDate),
})
export class UIFormDate extends BaseFormFieldComponent<string | Date | null> {
  @Input() override value: string | Date | null = '';
  @Input() override set config(configValue: IFormFieldDateConfig) {
    this.label = configValue.label;
    this.placeholder = configValue.placeholder ?? '';
    this.hideLabel = configValue.hideLabel ?? false;
    this.required = !!configValue.required;
    this.mode = configValue.mode ?? 'date';
    this.showTime = configValue.showTime ?? '';
    this.format = configValue.format ?? 'dd.MM.yyyy';
    this.maxDateOrCurrent = configValue.maxDateOrCurrent;
    this.minDateOrCurrent = configValue.minDateOrCurrent;
  }

  @Input() mode: FormFieldDateModeType = 'date';
  @Input() showTime?: string;
  @Input() format: DateFormatType = 'dd.MM.yyyy';
  @Input() maxDateOrCurrent?: Date | boolean;
  @Input() minDateOrCurrent?: Date | boolean;

  get maxDate(): Date | undefined {
    if (this.maxDateOrCurrent == null) {
      return undefined;
    }
    return typeof this.maxDateOrCurrent === 'boolean' ? new Date() : this.maxDateOrCurrent;
  }

  get minDate(): Date | undefined {
    if (this.minDateOrCurrent == null) {
      return undefined;
    }
    return typeof this.minDateOrCurrent === 'boolean' ? new Date() : this.minDateOrCurrent;
  }

  get showTimeFormat() {
    if (this.showTime !== '') {
      return { nzFormat: this.showTime };
    }
    return false;
  }

  disabledDate = (current: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentDay = new Date(current);
    currentDay.setHours(0, 0, 0, 0);

    let yearStart = today.getFullYear();

    if (today < new Date(yearStart, 8, 1)) {
      yearStart--;
    }

    const maxDate = this?.maxDateOrCurrent === true ? today : this?.maxDateOrCurrent;

    const minDate = this?.minDateOrCurrent === true ? today : this?.minDateOrCurrent;

    let beforeMaxDate = true;
    if (maxDate instanceof Date) {
      beforeMaxDate = currentDay <= maxDate;
    }

    let afterMinDate = true;
    if (minDate instanceof Date) {
      afterMinDate = currentDay >= minDate;
    }

    return !beforeMaxDate || !afterMinDate;
  };

  onDateChanged(value: Date | null): void {
    this.value = value?.toISOString() ?? null;
    this.onChange(this.value);
    this.updateControlValidity(); // Ensure control state is updated
  }

  // ControlValueAccessor
  override writeValue(value: string): void {
    this.value = value;
    this.updateControlValidity(); // Ensure control state is updated
  }

  onBlur(): void {
    this.onTouched();
  }
}
